// Categorical chart series colors derived from the GAIA brand hues.
// Order is load-bearing: adjacent slots were validated for colorblind
// separation (CVD ΔE ≥ 8) and ≥ 3:1 contrast against the card surface
// (#F8FAFC) — reorder only after re-validating.
export const statisticsChartSeriesColors = [
  '#2563EB',
  '#D97706',
  '#7C3AED',
  '#059669',
  '#DC2626',
  '#0284C7',
];

// Card surface the statistics charts render on (Naive Card background) —
// used for the separating gaps between stacked bar segments.
export const statisticsChartSurfaceColor = '#F8FAFC';

export type StatisticsDateRange = [number, number] | null;
export type StatisticsCountMap = Record<string, number> | null | undefined;
export type StatisticsEntriesSortMode = 'value-desc' | 'key-asc';

export interface StatisticsFiltersProps {
  includedOrgUids?: string[];
  excludedOrgUids?: string[];
  dateRange?: StatisticsDateRange;
}

function timestampToDateString(timestamp: number | undefined): string | undefined {
  if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) {
    return undefined;
  }

  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function buildStatisticsDateQuery(dateRange?: StatisticsDateRange): {
  start_date?: string;
  end_date?: string;
} {
  const [startTimestamp, endTimestamp] = dateRange ?? [];

  return {
    start_date: timestampToDateString(startTimestamp),
    end_date: timestampToDateString(endTimestamp),
  };
}

export function sortStatisticsEntries(
  source: StatisticsCountMap,
  sortMode: StatisticsEntriesSortMode
): [string, number][] {
  const entries = Object.entries(source ?? {});

  if (sortMode === 'key-asc') {
    return entries.sort(([leftKey], [rightKey]) => {
      const leftNumber = Number(leftKey);
      const rightNumber = Number(rightKey);

      if (!Number.isNaN(leftNumber) && !Number.isNaN(rightNumber)) {
        return leftNumber - rightNumber;
      }

      return leftKey.localeCompare(rightKey);
    });
  }

  return entries.sort(([leftKey, leftValue], [rightKey, rightValue]) => {
    if (leftValue === rightValue) {
      return leftKey.localeCompare(rightKey);
    }

    return rightValue - leftValue;
  });
}

/**
 * Human-readable number for stat tiles and chart labels: thousands separators,
 * fractions capped so raw backend averages (e.g. 56.041666666666664) never
 * overflow their tile.
 */
export function formatStatisticsNumber(
  value: number | string | null | undefined,
  maximumFractionDigits = 2
): string {
  const numeric = typeof value === 'string' ? Number(value) : value;

  if (typeof numeric !== 'number' || !Number.isFinite(numeric)) {
    return '—';
  }

  return numeric.toLocaleString('en-US', { maximumFractionDigits });
}

const MIME_TYPE_LABELS: Record<string, string> = {
  'application/pdf': 'PDF',
  'application/msword': 'Word (.doc)',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word (.docx)',
  'application/vnd.ms-excel': 'Excel (.xls)',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel (.xlsx)',
  'application/vnd.ms-powerpoint': 'PowerPoint (.ppt)',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint (.pptx)',
  'application/vnd.oasis.opendocument.text': 'OpenDocument text (.odt)',
  'application/vnd.oasis.opendocument.spreadsheet': 'OpenDocument sheet (.ods)',
  'application/vnd.oasis.opendocument.presentation': 'OpenDocument slides (.odp)',
  'application/zip': 'ZIP archive',
  'application/json': 'JSON',
  'application/xml': 'XML',
  'application/rtf': 'Rich text (.rtf)',
  'message/rfc822': 'Email (.eml)',
  'application/vnd.ms-outlook': 'Outlook (.msg)',
  'text/plain': 'Plain text',
  'text/html': 'HTML',
  'text/csv': 'CSV',
  'text/markdown': 'Markdown',
  'image/png': 'PNG image',
  'image/jpeg': 'JPEG image',
  'image/gif': 'GIF image',
  'image/svg+xml': 'SVG image',
  'image/tiff': 'TIFF image',
  'image/webp': 'WebP image',
};

/** Friendly display name for a MIME type ("application/pdf" → "PDF"). */
export function formatFileTypeLabel(mimeType: string): string {
  const known = MIME_TYPE_LABELS[mimeType.toLowerCase()];

  if (known) {
    return known;
  }

  const subtype = mimeType.includes('/') ? mimeType.slice(mimeType.indexOf('/') + 1) : mimeType;

  return formatStatisticsKeyLabel(subtype.replace(/^vnd\./, '').replace(/\./g, ' '));
}

export function formatStatisticsKeyLabel(value: string): string {
  if (/^\d+(\.\d+)?$/.test(value)) {
    return value;
  }

  return value
    .replace(/[_-]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(' ')
    .filter(part => part.length > 0)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}
